/**
 * Per-source-type nomenclature. A single source of truth for the user-facing noun a
 * data source's records are called — "tables" for relational, "collections" for MongoDB,
 * "files" for every file-folder source — plus the matching container nouns.
 *
 * Resolve from the two signals every stored connection carries: `engine` (the lowercased
 * type string, e.g. "postgresql", "mongodb", "file") and `datasourceType` (the backend
 * DatasourceType enum value, e.g. "FILE_PARQUET_FOLDER", "NOSQL"). Either may be absent at
 * a call site; the resolver tolerates nulls and falls back to relational ("tables").
 */
import { isFileLikeDatasourceType } from './fileTypeUtils';

// Engine strings that identify a file source even when `datasourceType` is unavailable:
// the UI file sentinel, the legacy sftp/s3 rows that predate typed FILE_*_FOLDER, and
// the lowercased vfsScheme values file rows carry as their engine (datasourcesApi).
const FILE_ENGINES = new Set(['file', 's3', 'sftp', 'local', 'azure_blob', 'sharepoint']);

const FILE_NOMENCLATURE = {
  item: 'files',
  itemSingular: 'file',
  container: 'folder',
  containerSchema: null,
  record: 'records',
};

const MONGO_NOMENCLATURE = {
  item: 'collections',
  itemSingular: 'collection',
  container: 'database',
  containerSchema: null,
  record: 'documents',
};

const RELATIONAL_NOMENCLATURE = {
  item: 'tables',
  itemSingular: 'table',
  container: 'database',
  containerSchema: 'schema',
  record: 'rows',
};

/**
 * Resolve the noun set for a data source. `datasourceType` wins when it identifies a file
 * source (engine is opaque on the wire for file rows); otherwise we branch on `engine`.
 * Anything unrecognised — including DynamoDB, whose own term IS "tables" — falls through
 * to the relational default.
 */
export function getSourceNomenclature(engine, datasourceType) {
  if (isFileLikeDatasourceType(datasourceType)) return FILE_NOMENCLATURE;

  const e = typeof engine === 'string' ? engine.toLowerCase() : '';
  if (FILE_ENGINES.has(e)) return FILE_NOMENCLATURE;
  if (e === 'mongodb') return MONGO_NOMENCLATURE;
  return RELATIONAL_NOMENCLATURE;
}

/**
 * Label for a run's work items, keyed on the backend `workItemKind` (TABLES / FILES) — the
 * per-run truth used in progress lines ("X of Y {label} processed"). Distinct from
 * getSourceNomenclature, which labels by source type; a run reports its own unit. `fallback`
 * is the noun used when the kind is absent (default "records"; discovery landscapes that
 * assume relational pass "tables").
 */
export function workItemLabel(kind, fallback = 'records') {
  if (!kind) return fallback;
  switch (String(kind).toUpperCase()) {
    case 'TABLES': return 'tables';
    case 'FILES': return 'files';
    default: return String(kind).toLowerCase();
  }
}
